import { useEffect, useMemo, useRef, useState } from "react";
import { adaptWorkspaceLive } from "./workspaceLiveAdapter";
import { lookupStrategyId } from "@/workspace/handoff";
import {
  createRequestLog,
  getRankedStrategies,
  getRegimeBacktestContext,
  getRegimeHistory,
  getShadowMoney,
  getTickerData,
  getTickerTalk,
} from "./workspaceLiveQueries";

function canceled(err) {
  return err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError";
}

function emptyResources() {
  return {
    tickerData: { status: "idle", data: null, error: null, fetchedAt: null },
    regimeHistory: { status: "idle", data: null, error: null, fetchedAt: null },
    rankedStrategies: { status: "idle", data: null, error: null, fetchedAt: null },
    regimeBacktestContext: { status: "idle", data: null, error: null, fetchedAt: null, forStrategy: null },
    tickerTalk: { status: "idle", data: null, error: null, fetchedAt: null },
    shadowMoney: { status: "idle", data: null, error: null, fetchedAt: null },
  };
}

export function useWorkspaceLive(symbol, tab, { strategyLookup } = {}) {
  const [resources, setResources] = useState(emptyResources);
  const [logEntries, setLogEntries] = useState([]);
  const cacheRef = useRef({ symbol: null, bag: emptyResources() });
  const logRef = useRef(createRequestLog());
  const genRef = useRef(0);

  useEffect(() => {
    if (!symbol) return undefined;
    const upper = String(symbol).toUpperCase();
    if (cacheRef.current.symbol !== upper) {
      cacheRef.current = { symbol: upper, bag: emptyResources() };
      logRef.current = createRequestLog();
      setResources(emptyResources());
      setLogEntries([]);
    }
    const bag = cacheRef.current.bag;
    const ac = new AbortController();
    const gen = ++genRef.current;
    const log = logRef.current;

    const put = (key, patch) => {
      if (gen !== genRef.current) return;
      bag[key] = { ...bag[key], ...patch };
      setResources({ ...bag });
      setLogEntries([...log.entries]);
    };

    const load = async (key, fn) => {
      if (bag[key].status === "ready") return bag[key];
      put(key, { status: "loading", error: null });
      try {
        const { data } = await fn();
        const fetchedAt = new Date().toISOString();
        put(key, { status: "ready", data, error: null, fetchedAt });
        return bag[key];
      } catch (err) {
        if (canceled(err) || gen !== genRef.current) {
          if (canceled(err) && gen === genRef.current) put(key, { status: "idle" });
          return bag[key];
        }
        put(key, {
          status: "error",
          error: err?.response?.data?.detail || err?.message || "data unavailable",
        });
        return bag[key];
      }
    };

    const run = async () => {
      await load("tickerData", () => getTickerData(upper, { signal: ac.signal, tab, log }));

      if (tab === "overview" || tab === "regime") {
        await load("regimeHistory", () => getRegimeHistory(upper, { signal: ac.signal, tab, log }));
      }

      const needRanked = tab === "options" || Boolean(strategyLookup);
      if (needRanked) {
        await load("rankedStrategies", () => getRankedStrategies(upper, { signal: ac.signal, tab, log }));
      }

      if (tab === "options") {
        const ranked = bag.rankedStrategies.data;
        const strategyId = lookupStrategyId(strategyLookup, ranked);
        if (bag.regimeBacktestContext.forStrategy !== strategyId) {
          bag.regimeBacktestContext = {
            status: "idle",
            data: null,
            error: null,
            fetchedAt: null,
            forStrategy: strategyId,
          };
        }
        if (strategyId) {
          await load("regimeBacktestContext", () => (
            getRegimeBacktestContext(upper, strategyId, { signal: ac.signal, tab, log })
          ));
          if (gen === genRef.current) {
            put("regimeBacktestContext", { forStrategy: strategyId });
          }
        } else {
          put("regimeBacktestContext", {
            status: "skipped",
            data: null,
            error: null,
            forStrategy: null,
          });
        }
      }
      if (tab === "talk") {
        await load("tickerTalk", () => getTickerTalk(upper, { signal: ac.signal, tab, log }));
      }
      if (tab === "ownership") {
        await load("shadowMoney", () => getShadowMoney(upper, { signal: ac.signal, tab, log }));
      }
    };

    run();
    return () => {
      ac.abort();
      Object.keys(bag).forEach((k) => {
        if (bag[k].status === "loading") bag[k] = { ...bag[k], status: "idle" };
      });
    };
  }, [symbol, tab, strategyLookup]);

  const ticker = useMemo(() => {
    if (!resources.tickerData.data) return null;
    const vm = adaptWorkspaceLive({
      symbol,
      tickerData: resources.tickerData.data,
      history: resources.regimeHistory.data,
      historyStatus: resources.regimeHistory.status,
      ranked: resources.rankedStrategies.data,
      rankedStatus: resources.rankedStrategies.status,
      requestedStrategy: strategyLookup || null,
      backtestCtx: resources.regimeBacktestContext.data,
      talk: resources.tickerTalk.data,
      shadow: resources.shadowMoney.data,
      fetchedAt: resources.tickerData.fetchedAt,
    });
    vm.resourceStatus = {
      history: resources.regimeHistory.status,
      historyError: resources.regimeHistory.error,
      ranked: resources.rankedStrategies.status,
      talk: resources.tickerTalk.status,
      talkError: resources.tickerTalk.error,
      ownership: resources.shadowMoney.status,
      ownershipError: resources.shadowMoney.error,
      context: resources.regimeBacktestContext.status,
      contextStrategy: resources.regimeBacktestContext.forStrategy || null,
    };
    return vm;
  }, [symbol, strategyLookup, resources]);

  const loadingHeader = resources.tickerData.status === "idle" || resources.tickerData.status === "loading";
  const headerError = resources.tickerData.status === "error" ? resources.tickerData.error : null;

  return {
    ticker,
    resources,
    requestLog: logEntries,
    loadingHeader,
    headerError,
    writes: 0,
    externalProviderCalls: 0,
  };
}
